import type { ReactNode } from 'react';

import type { SanityTextBlock } from '@/sanity/lib/sections';
import { cn } from '@/lib/utils';

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function extractSpanText(raw: unknown): string {
  if (!isRecord(raw)) return '';
  if (typeof raw.text === 'string') return raw.text;
  if (typeof raw.value === 'string') return raw.value;
  return '';
}

function extractMarks(raw: unknown): string[] {
  if (!isRecord(raw)) return [];
  const m = raw.marks;
  if (!Array.isArray(m)) return [];
  return m.filter((x): x is string => typeof x === 'string');
}

export function sanityTextToPlainText(blocks?: SanityTextBlock[] | null): string {
  if (!blocks?.length) return '';
  return blocks
    .map((block) => {
      const b = block as unknown as Record<string, unknown>;
      if (typeof b.text === 'string') return b.text;
      const children = b.children;
      if (!Array.isArray(children)) return '';
      return children.map((child) => extractSpanText(child)).join('');
    })
    .filter(Boolean)
    .join('\n');
}

function renderSpan(
  span: unknown,
  key: string,
  emphasisClassName?: string,
  strongClassName?: string,
) {
  const text = extractSpanText(span);
  if (!text) return null;

  const marks = extractMarks(span);
  const hasEmphasis = marks.includes('em');
  const hasStrong = marks.includes('strong');

  if (!hasEmphasis && !hasStrong) {
    return <span key={key}>{text}</span>;
  }

  return (
    <span
      key={key}
      className={cn(
        hasEmphasis && (emphasisClassName ?? 'italic font-normal'),
        hasStrong && (strongClassName ?? 'font-medium'),
      )}
    >
      {text}
    </span>
  );
}

export function SanityInlineText({
  blocks,
  fallback = '',
  emphasisClassName,
  strongClassName,
}: {
  blocks?: SanityTextBlock[] | null;
  fallback?: ReactNode;
  emphasisClassName?: string;
  strongClassName?: string;
}) {
  if (!blocks?.length) return fallback;

  return (
    <>
      {blocks.map((block, blockIndex) => {
        const b = block as unknown as Record<string, unknown>;
        const blockType = b._type;
        if (blockType != null && blockType !== 'block') {
          return null;
        }
        const rawChildren = b.children;
        const childList = Array.isArray(rawChildren) ? rawChildren : [];
        const children = childList.map((span, spanIndex) =>
          renderSpan(
            span,
            isRecord(span) && typeof span._key === 'string'
              ? span._key
              : `${String(b._key ?? blockIndex)}-${spanIndex}`,
            emphasisClassName,
            strongClassName,
          ),
        );

        return (
          <span key={typeof b._key === 'string' ? b._key : blockIndex}>
            {blockIndex > 0 ? <br /> : null}
            {children}
          </span>
        );
      })}
    </>
  );
}
